import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck, Waves, HeartHandshake, Compass, Anchor, Map, LifeBuoy,
  CalendarClock, ClipboardCheck, CreditCard, Sparkles, Quote, ChevronLeft, ChevronRight, Clock, Users, Play, Film, MessageSquare, Copy, Ticket,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Bubbles, WaveDivider, useReveal } from "@/components/Motifs";
import api, { formatApiError, BACKEND_URL } from "@/lib/api";

const benefits = [
  { icon: Waves, title: "Build Water Confidence", text: "Calm, gentle progressions help children feel safe, capable and joyful in the water." },
  { icon: LifeBuoy, title: "Learn Life-Saving Float Skills", text: "The essential swim-float-swim sequence gives your child a critical layer of protection." },
  { icon: HeartHandshake, title: "Receive Individual Attention", text: "Every lesson is one-on-one, tailored to your child's pace, personality and comfort." },
];

const steps = [
  { icon: Compass, title: "Choose a lesson or package", text: "Browse programs and pick the adventure that fits your child." },
  { icon: CalendarClock, title: "Select an available time", text: "See open times and reserve the slot that works for your family." },
  { icon: CreditCard, title: "Complete registration & payment", text: "Fill out the forms and pay securely online with Stripe." },
  { icon: Sparkles, title: "Begin the water-safety adventure", text: "Arrive and watch your little mermaid or pirate shine." },
];

export default function Landing() {
  const reveal = useReveal();
  const [content, setContent] = useState({});
  const [programs, setPrograms] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [videos, setVideos] = useState([]);
  const [graduates, setGraduates] = useState([]);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
    api.get("/programs").then((r) => setPrograms(r.data)).catch(() => {});
    api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/videos").then((r) => setVideos(r.data)).catch(() => {});
    api.get("/graduates").then((r) => setGraduates(r.data)).catch(() => {});
  }, []);

  return (
    <div ref={reveal} id="top" className="bg-cream">
      <Navbar />
      <Hero content={content} />
      <WhyFloatNow content={content} />
      <Programs programs={programs} />
      <Instructor content={content} />
      <HowItWorks />
      <VideoGallery items={videos} />
      <Graduates items={graduates} />
      <ParentInfo faqs={faqs} />
      <Testimonials items={testimonials} />
      <FinalCTA content={content} />
      <Footer />
    </div>
  );
}

/* -------------------------------------------------------------- Hero */
function Hero({ content }) {
  return (
    <section className="relative min-h-[92vh] flex items-center overflow-hidden">
      <img src="/assets/mermaid1.webp" alt="Little mermaid at golden hour by the sea" className="absolute inset-0 h-full w-full object-cover object-[70%_30%] sm:object-[center_35%] brightness-110" />
      <div className="absolute inset-0 bg-gradient-to-r from-navy/70 via-navy/40 to-navy/10" />
      <Bubbles count={18} />
      <div className="relative z-10 mx-auto max-w-7xl px-6 py-24 w-full">
        <div className="max-w-2xl" data-reveal>
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-4 py-1.5 text-cream text-xs tracking-[0.18em] uppercase border border-white/20">
            <Compass size={14} className="text-gold" /> North Platte, Nebraska
          </span>
          <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05]">
            {content.hero_headline || "Confidence Begins with the First Float."}
          </h1>
          <p className="mt-6 text-lg text-cream/90 max-w-xl leading-relaxed">{content.hero_sub}</p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Button asChild className="rounded-full bg-coral hover:bg-[#C95B50] text-white px-8 h-13 py-6 text-base shadow-xl shadow-coral/30" data-testid="hero-book">
              <Link to="/book"><Anchor size={18} className="mr-2" /> Book a Lesson</Link>
            </Button>
            <WaitlistDialog trigger={
              <Button variant="outline" className="rounded-full border-2 border-cream/70 bg-transparent text-cream hover:bg-cream hover:text-navy px-8 py-6 text-base" data-testid="hero-waitlist">
                Join the Waiting List
              </Button>
            } />
          </div>
          <p className="mt-8 text-cream/70 italic font-serif text-lg">"{content.tagline}"</p>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 z-10"><WaveDivider color="#F9F6F0" /></div>
    </section>
  );
}

/* -------------------------------------------------------------- Why */
function WhyFloatNow({ content }) {
  return (
    <section className="relative py-24 bg-cream">
      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center max-w-3xl mx-auto" data-reveal>
          <p className="text-xs tracking-[0.2em] uppercase text-teal font-semibold">Why Float Now</p>
          <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-navy">A Brave Little Start, Guided With Care.</h2>
          <p className="mt-5 text-navy/70 text-lg leading-relaxed">{content.why_text}</p>
        </div>
        <div className="mt-16 grid gap-8 md:grid-cols-3">
          {benefits.map((b) => (
            <div key={b.title} data-reveal className="group rounded-3xl bg-white p-8 border border-gold/30 shadow-[0_8px_32px_rgba(15,32,39,0.06)] hover:shadow-[0_12px_40px_rgba(15,32,39,0.12)] hover:-translate-y-1 transition-[transform,box-shadow] duration-300">
              <div className="h-14 w-14 grid place-items-center rounded-2xl bg-teal/10 text-teal group-hover:bg-teal group-hover:text-white transition-colors">
                <b.icon size={26} />
              </div>
              <h3 className="mt-6 font-serif text-2xl text-navy">{b.title}</h3>
              <p className="mt-3 text-navy/70 leading-relaxed">{b.text}</p>
            </div>
          ))}
        </div>
        <div data-reveal className="mt-14 mx-auto max-w-4xl rounded-3xl bg-navy p-8 sm:p-10 text-cream flex gap-5 items-start relative overflow-hidden">
          <div className="absolute inset-0 fn-scales opacity-30" />
          <ShieldCheck size={40} className="relative shrink-0 text-gold" />
          <p className="relative leading-relaxed text-cream/90">{content.safety_statement}</p>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- Programs */
function Programs({ programs }) {
  const navigate = useNavigate();
  return (
    <section id="programs" className="relative py-24 bg-white fn-scales">
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-2xl" data-reveal>
          <p className="text-xs tracking-[0.2em] uppercase text-coral font-semibold">Choose Your Adventure</p>
          <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-navy">Programs For Every Little Swimmer.</h2>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {programs.map((p, i) => {
            const isSignature = (p.name || "").toLowerCase().includes("float 4 life");
            return (
            <div key={p.id} data-reveal
              className={`group rounded-3xl overflow-hidden bg-cream border border-gold/30 shadow-[0_8px_32px_rgba(15,32,39,0.06)] hover:shadow-[0_16px_48px_rgba(15,32,39,0.14)] hover:-translate-y-1.5 transition-[transform,box-shadow] duration-300 flex flex-col ${isSignature ? "ring-2 ring-teal" : ""}`}>
              {p.image_url && (
                <div className="aspect-[3/4] overflow-hidden relative bg-gradient-to-b from-navy/10 via-teal/10 to-cream">
                  <img src={p.image_url} alt={p.name} className="h-full w-full object-contain group-hover:scale-105 transition-transform duration-500" />
                </div>
              )}
              <div className="p-7 flex flex-col flex-1">
                {isSignature && <span className="self-start mb-3 rounded-full bg-teal text-white text-[10px] tracking-[0.15em] uppercase px-3 py-1">Signature</span>}
                <h3 className="font-serif text-2xl text-navy">{p.name}</h3>
                <div className="mt-3 flex flex-wrap gap-3 text-xs text-navy/70">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-lavender/15 text-navy px-3 py-1"><Users size={13} /> {p.ages}</span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-teal/15 text-navy px-3 py-1"><Clock size={13} /> {p.length_min} min</span>
                </div>
                <p className="mt-4 text-navy/70 text-sm leading-relaxed flex-1">{p.description}</p>
                <div className="mt-6 flex items-center justify-between">
                  <span className="font-serif text-3xl text-coral">${Number(p.price).toFixed(0)}</span>
                  <Button onClick={() => navigate(`/book?program=${p.id}`)} data-testid={`program-book-${i}`}
                    className="rounded-full bg-navy hover:bg-teal text-cream px-6 transition-colors">Book Now</Button>
                </div>
              </div>
            </div>
            );
          })}
        </div>
        <div className="mt-12 text-center" data-reveal>
          <Button onClick={() => navigate("/book?view=packages")} data-testid="see-packages-btn" variant="outline" className="rounded-full border-teal text-teal hover:bg-teal hover:text-white px-7 py-6 text-base"><Ticket size={18} className="mr-2" />See lesson packages &amp; save</Button>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- Instructors */
function Instructor({ content }) {
  const instructors = [
    { img: "/assets/mermaid2.webp", name: content.instructor_name, title: content.instructor_title, bio: content.instructor_bio },
    { img: "/assets/kristi.webp", name: content.instructor2_name, title: content.instructor2_title, bio: content.instructor2_bio },
  ];
  return (
    <section id="about" className="relative py-24 bg-cream overflow-hidden">
      <div className="mx-auto max-w-6xl px-6">
        <div className="text-center max-w-2xl mx-auto" data-reveal>
          <p className="text-xs tracking-[0.2em] uppercase text-teal font-semibold">Meet Your Instructors</p>
          <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-navy">Warm, Trusted Guides In The Water.</h2>
          <div className="mt-5 flex justify-center gap-6 text-navy/70 text-sm flex-wrap">
            <span className="flex items-center gap-2"><HeartHandshake size={18} className="text-teal" /> No shame, no coercion</span>
            <span className="flex items-center gap-2"><ShieldCheck size={18} className="text-teal" /> Emotional safety first</span>
          </div>
        </div>
        <div className="mt-16 grid gap-12 md:grid-cols-2">
          {instructors.map((ins, i) => (
            <div key={ins.name || i} data-reveal className="flex flex-col">
              <div className="relative">
                <div className={`absolute -inset-3 rounded-[2rem] ${i === 0 ? "bg-teal/20 -rotate-2" : "bg-lavender/25 rotate-2"}`} />
                <img src={ins.img} alt={ins.name} className="relative rounded-[2rem] object-cover w-full h-[440px] shadow-2xl" />
              </div>
              <div className="mt-8">
                <h3 className="font-serif text-3xl text-navy">{ins.name}</h3>
                <p className="mt-1 text-coral font-medium">{ins.title}</p>
                <p className="mt-4 text-navy/75 leading-relaxed">{ins.bio}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- How It Works */
function HowItWorks() {
  return (
    <section className="relative py-24 bg-navy text-cream overflow-hidden">
      <Bubbles count={12} dark />
      <div className="relative mx-auto max-w-7xl px-6">
        <div className="text-center max-w-2xl mx-auto" data-reveal>
          <p className="text-xs tracking-[0.2em] uppercase text-gold font-semibold flex items-center justify-center gap-2"><Map size={16} /> How It Works</p>
          <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-white">Four Simple Steps To The Water.</h2>
        </div>
        <div className="mt-16 grid gap-8 md:grid-cols-4 relative">
          <div className="hidden md:block absolute top-7 left-[12%] right-[12%] h-[2px] fn-map-dash opacity-60" />
          {steps.map((s, i) => (
            <div key={s.title} data-reveal className="relative text-center">
              <div className="mx-auto h-14 w-14 grid place-items-center rounded-full bg-cream text-navy border-4 border-gold relative z-10">
                <s.icon size={24} />
              </div>
              <span className="mt-4 inline-block font-serif text-gold text-lg">Step {i + 1}</span>
              <h3 className="mt-1 font-serif text-xl text-white">{s.title}</h3>
              <p className="mt-2 text-cream/70 text-sm leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- Video Gallery */
function ytEmbed(url) {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}
function platformLabel(url) {
  const u = url.toLowerCase();
  if (u.includes("facebook")) return "Facebook";
  if (u.includes("instagram")) return "Instagram";
  if (u.includes("tiktok")) return "TikTok";
  if (u.includes("youtu")) return "YouTube";
  return "Video";
}
function VideoGallery({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <section className="relative py-24 bg-cream">
      <div className="mx-auto max-w-6xl px-6">
        <div className="text-center max-w-2xl mx-auto" data-reveal>
          <p className="text-xs tracking-[0.2em] uppercase text-coral font-semibold flex items-center justify-center gap-2"><Film size={16} /> Watch &amp; Learn</p>
          <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-navy">See The Magic In The Water.</h2>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {items.map((v) => {
            const embed = ytEmbed(v.url);
            return (
              <div key={v.id} data-reveal className="rounded-3xl overflow-hidden bg-white border border-gold/30 shadow-[0_8px_32px_rgba(15,32,39,0.06)]">
                {embed ? (
                  <div className="aspect-video"><iframe src={embed} title={v.title || "Video"} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>
                ) : (
                  <a href={v.url} target="_blank" rel="noreferrer" data-testid={`video-link-${v.id}`} className="group block aspect-video bg-navy relative grid place-items-center">
                    <div className="absolute inset-0 fn-scales opacity-30" />
                    <div className="relative h-16 w-16 grid place-items-center rounded-full bg-coral text-white group-hover:scale-110 transition-transform"><Play size={26} /></div>
                    <span className="absolute bottom-3 left-4 text-cream/80 text-xs tracking-wide uppercase">{platformLabel(v.url)}</span>
                  </a>
                )}
                {v.title && <p className="p-4 font-serif text-lg text-navy">{v.title}</p>}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- Graduates */
function Graduates({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <section className="relative py-24 bg-white fn-scales" data-testid="graduates-section">
      <div className="mx-auto max-w-6xl px-6">
        <div className="text-center max-w-2xl mx-auto" data-reveal>
          <p className="text-xs tracking-[0.2em] uppercase text-coral font-semibold flex items-center justify-center gap-2"><Sparkles size={16} /> Wall of Fame</p>
          <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-navy">Brave Little Swimmers.</h2>
          <p className="mt-4 text-navy/70">Celebrating the mermaids and pirates who've earned their water-safety wings.</p>
        </div>
        <div className="mt-14 grid gap-6 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {items.map((g) => (
            <div key={g.id} data-reveal data-testid="graduate-card" className="group rounded-3xl overflow-hidden bg-cream border border-gold/30 shadow-[0_8px_32px_rgba(15,32,39,0.06)] hover:-translate-y-1.5 transition-transform duration-300">
              <div className="aspect-square overflow-hidden">
                <img src={g.photo_url} alt={g.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-4 text-center">
                <p className="font-serif text-lg text-navy">{g.name}</p>
                {g.caption && <p className="text-xs text-navy/60 mt-0.5">{g.caption}</p>}
                <div className="mt-3 flex items-center justify-center gap-2">
                  <a href={`sms:?&body=${encodeURIComponent(`Check out ${g.name} on Float Now's Wall of Fame! ${BACKEND_URL}/api/share/graduate/${g.id}`)}`} data-testid={`share-sms-${g.id}`} title="Share by text message" className="inline-flex items-center gap-1.5 rounded-full border border-teal/40 text-teal hover:bg-teal/10 text-xs font-medium px-3 py-1.5 transition-colors"><MessageSquare size={14} />Text</a>
                  <button onClick={async () => { try { await navigator.clipboard.writeText(`${BACKEND_URL}/api/share/graduate/${g.id}`); toast.success("Link copied!"); } catch { toast.error("Couldn't copy link."); } }} data-testid={`share-copy-${g.id}`} title="Copy link" className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 text-navy hover:border-teal text-xs font-medium px-3 py-1.5 transition-colors"><Copy size={14} />Copy</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- Parent Info / FAQ */
function ParentInfo({ faqs }) {
  return (
    <section id="faq" className="py-24 bg-cream">
      <div className="mx-auto max-w-3xl px-6">
        <div className="text-center" data-reveal>
          <p className="text-xs tracking-[0.2em] uppercase text-teal font-semibold">Parent Information</p>
          <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-navy">Answers For Busy Parents.</h2>
        </div>
        <div data-reveal className="mt-12">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((f, i) => (
              <AccordionItem key={f.id} value={`item-${i}`} className="border-b border-navy/15">
                <AccordionTrigger data-testid={`faq-${i}`} className="text-left font-serif text-lg text-navy hover:text-coral hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-navy/70 leading-relaxed text-base">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- Testimonials */
function Testimonials({ items }) {
  const [idx, setIdx] = useState(0);
  const list = items.length ? items : [];
  const current = list[idx % (list.length || 1)];
  if (!list.length) return null;
  return (
    <section className="relative py-24 bg-white fn-scales">
      <div className="mx-auto max-w-4xl px-6 text-center">
        <p className="text-xs tracking-[0.2em] uppercase text-coral font-semibold" data-reveal>Testimonials</p>
        <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-navy" data-reveal>Words From Our Families.</h2>
        <div data-reveal className="mt-12 rounded-3xl bg-cream border border-gold/30 p-10 sm:p-14 shadow-[0_8px_32px_rgba(15,32,39,0.06)] relative">
          <Quote size={44} className="mx-auto text-teal/40" />
          <p className="mt-6 font-serif text-2xl text-navy leading-snug italic">{current?.quote}</p>
          <p className="mt-6 text-sm text-navy/60">{current?.author}{current?.placeholder ? "" : ""}</p>
          {current?.placeholder && <p className="mt-2 text-xs text-coral/80">Genuine reviews will appear here once entered.</p>}
          {list.length > 1 && (
            <div className="mt-8 flex items-center justify-center gap-4">
              <button onClick={() => setIdx((idx - 1 + list.length) % list.length)} data-testid="testimonial-prev" className="h-10 w-10 grid place-items-center rounded-full border border-navy/20 text-navy hover:bg-navy hover:text-cream transition-colors"><ChevronLeft size={18} /></button>
              <span className="text-sm text-navy/50">{(idx % list.length) + 1} / {list.length}</span>
              <button onClick={() => setIdx((idx + 1) % list.length)} data-testid="testimonial-next" className="h-10 w-10 grid place-items-center rounded-full border border-navy/20 text-navy hover:bg-navy hover:text-cream transition-colors"><ChevronRight size={18} /></button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- Final CTA */
function FinalCTA({ content }) {
  return (
    <section className="relative py-28 overflow-hidden">
      <img src="/assets/mermaid3.webp" alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-navy/75" />
      <Bubbles count={14} />
      <div className="relative z-10 mx-auto max-w-3xl px-6 text-center" data-reveal>
        <h2 className="font-serif text-4xl sm:text-5xl text-white leading-tight">Your Child's Safer Water Adventure Can Start Now.</h2>
        <p className="mt-5 text-cream/80 text-lg italic font-serif">"{content.tagline_sub}"</p>
        <div className="mt-9 flex flex-wrap justify-center gap-4">
          <Button asChild className="rounded-full bg-coral hover:bg-[#C95B50] text-white px-8 py-6 text-base shadow-xl shadow-coral/30" data-testid="cta-book">
            <Link to="/book"><Anchor size={18} className="mr-2" /> Schedule a Lesson</Link>
          </Button>
          <WaitlistDialog trigger={
            <Button variant="outline" className="rounded-full border-2 border-cream/70 bg-transparent text-cream hover:bg-cream hover:text-navy px-8 py-6 text-base" data-testid="cta-waitlist">Join the Waiting List</Button>
          } />
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- Waitlist Dialog */
export function WaitlistDialog({ trigger }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/waitlist", form);
      toast.success("You're on the waiting list! We'll be in touch soon.");
      setOpen(false);
      setForm({ name: "", email: "", phone: "", note: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-cream">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-navy">Join the Waiting List</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Name</Label><Input required data-testid="waitlist-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-white" /></div>
          <div><Label>Email</Label><Input type="email" required data-testid="waitlist-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-white" /></div>
          <div><Label>Phone (optional)</Label><Input data-testid="waitlist-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 bg-white" /></div>
          <div><Label>Notes (optional)</Label><Textarea data-testid="waitlist-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1 bg-white" /></div>
          <Button type="submit" disabled={loading} data-testid="waitlist-submit" className="w-full rounded-full bg-coral hover:bg-[#C95B50] text-white">{loading ? "Joining…" : "Join Waiting List"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
